import { createRef, memo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import "./SignupMarketing.scss";
import { scrollToElement } from "app/utils/scroller";
import { signupPageAuthShape, resolutionPropType } from "app/utils/propTypes";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { Route, Routes, useNavigate } from "react-router-dom";
import LoginContainer from "app/pages/Auth/Signin/LoginContainer";
import SignupContainer from "app/pages/Auth/Signup/SignupContainer";
import ChangePasswordContainer from "app/pages/Auth/ChangePassword/ChangePasswordContainer";
import ResetPasswordContainer from "app/pages/Auth/ResetPassword/ResetPasswordContainer";
import SignupPageBackground from "app/pages/SignupPage/Auth/SignupMarketing/SignupPageBackground";

const BACKGROUND_MOBILE_HEIGHT = {
	SMALL: "small",
	LARGE: "large",
};

const SignupMarketing = ({ resolution, signupPage = {}, productListHeaderRef }) => {
	const navigate = useNavigate();
	const authRef = createRef();

	const handleScroll = useCallback(() => {
		if (resolution === RESOLUTION.SMALL || resolution === RESOLUTION.MEDIUM) {
			scrollToElement({ element: authRef.current, disableSmoothScroll: false });
		} else {
			scrollToElement({ element: productListHeaderRef.current, disableSmoothScroll: false });
		}
	}, [authRef]);

	const photoClass = classNames({
		"signup-marketing__photo": true,
		"signup-marketing__photo--small":
			signupPage.photoSmallBackgroundHeight === BACKGROUND_MOBILE_HEIGHT.SMALL,
	});

	const redirectOnAuthSuccess = useCallback(() => {
		const redirectUrl = signupPage.redirectUrl;
		if (redirectUrl) {
			window.location = redirectUrl;
		} else {
			navigate({ pathname: "/listing" });
		}
	}, [signupPage.redirectUrl]);

	return (
		<div className="signup-marketing" data-testid="signup-marketing">
			<section className="signup-marketing__hero">
				<div
					className={photoClass}
					data-testid={`signup-marketing-photo-${signupPage.photoSmallBackgroundHeight}`}
				>
					{/* Les photos provenant du studio sont au ratio 1440x550 */}
					<SignupPageBackground
						resolution={resolution}
						backgroundPosition={signupPage.backgroundPosition}
						sourceForegroundLargeUrl={signupPage.photoLargeTextUrl}
						sourceBackgroundLargeUrl={signupPage.photoLargeBackgroundUrl}
						sourceForegroundSmallUrl={signupPage.photoSmallTextUrl}
						sourceBackgroundSmallUrl={signupPage.photoSmallBackgroundUrl}
						sourceBackgroundLargeThumbnail={signupPage.photoLargeBackgroundThumbnail}
						sourceBackgroundSmallThumbnail={signupPage.photoSmallBackgroundThumbnail}
					/>
					<i
						className="icon icon--more-below-white signup-marketing__more-below"
						onClick={handleScroll}
					/>
				</div>

				<div className="signup-marketing__container">
					<div className="signup-marketing__login" ref={authRef}>
						<Routes>
							<Route
								path="/login"
								element={
									<LoginContainer
										homeRedirection={false}
										onAuthSuccess={redirectOnAuthSuccess}
									/>
								}
							/>
							<Route
								path="/signup"
								element={
									<SignupContainer
										headline={signupPage.signupFormHeadline}
										onAuthSuccess={redirectOnAuthSuccess}
									/>
								}
							/>

							<Route path="/changepassword" element={<ChangePasswordContainer />} />
							<Route path="/resetpassword" element={<ResetPasswordContainer />} />
							<Route
								path="/"
								element={
									<SignupContainer
										headline={signupPage.signupFormHeadline}
										onAuthSuccess={redirectOnAuthSuccess}
									/>
								}
							/>
						</Routes>
					</div>
				</div>
			</section>
		</div>
	);
};

SignupMarketing.propTypes = {
	productListHeaderRef: PropTypes.object,
	resolution: resolutionPropType,
	signupPage: signupPageAuthShape,
};

export default memo(SignupMarketing);
